import { config } from '../core/config.js';

const TOGGLES = ['once', 'replay', 'bothways', 'reverse'];

/**
 * Play a paused tween when the element's top reaches the start line.
 *
 * The start line sits at `start` % of the viewport height, measured from the top
 * ('80%' = 80% down the screen). Elements already above the line (e.g. page
 * reloaded mid-scroll) play immediately so they never stay hidden.
 *
 * `toggle` decides what happens on later passes:
 *   once     — plays the first time, never again
 *   replay   — replays each time the line is crossed scrolling down
 *   bothways — replays when scrolling down past the line and when coming back from the top
 *   reverse  — plays scrolling down, rewinds when scrolling back up past the line
 *
 * `replay` and `bothways` reset the tween only once the element is fully off-screen,
 * so it never snaps back while visible.
 *
 * @param {HTMLElement} el
 * @param {gsap.core.Animation} tween - Built with `paused: true`
 * @param {object} [options]
 * @param {string} [options.start]  - '80%', '80vh' or '80'. Falls back to config.start
 * @param {string} [options.toggle] - once | replay | bothways | reverse. Falls back to config.toggle
 */
export function playOnScroll(el, tween, { start, toggle } = {}) {
  const percent = toPercent(start) ?? toPercent(config.start) ?? 80;
  const mode = [toggle, config.toggle].find(t => TOGGLES.includes(t)) ?? 'replay';

  if (!('IntersectionObserver' in window)) {
    tween.play();
    return;
  }

  let wasAbove = null;
  let isReset = true;

  const reset = () => {
    tween.pause().progress(0);
    isReset = true;
  };
  const play = () => {
    tween.play();
    isReset = false;
  };

  // Crossing the start line
  const lineObserver = new IntersectionObserver(([entry]) => {
    const line = (entry.rootBounds?.height ?? window.innerHeight) * percent / 100;
    const rect = entry.boundingClientRect;
    const above = rect.top <= line;
    if (above === wasAbove) return;
    wasAbove = above;

    if (above) {
      // bothways: an element already off-screen above waits to come back into view
      if (mode === 'bothways' && rect.bottom <= 0) return;
      play();
      if (mode === 'once') {
        lineObserver.disconnect();
        viewportObserver?.disconnect();
      }
    } else if (mode === 'reverse') {
      tween.reverse();
    }
  }, {
    // Shrink the root from the bottom so it ends at the start line
    rootMargin: `0px 0px -${100 - percent}% 0px`,
  });

  // Leaving / re-entering the viewport — only needed to reset and replay
  const viewportObserver = (mode === 'replay' || mode === 'bothways')
    ? new IntersectionObserver(([entry]) => {
        const rect = entry.boundingClientRect;
        if (!entry.isIntersecting) {
          const leftBelow = rect.top > 0;
          if (leftBelow || mode === 'bothways') reset();
          return;
        }
        // bothways: coming back into view from the top
        const line = (entry.rootBounds?.height ?? window.innerHeight) * percent / 100;
        if (mode === 'bothways' && isReset && rect.top <= line) play();
      })
    : null;

  lineObserver.observe(el);
  viewportObserver?.observe(el);
}

function toPercent(value) {
  if (value === undefined || value === null || value === '') return null;
  const n = parseFloat(String(value));
  if (Number.isNaN(n)) return null;
  return Math.min(100, Math.max(0, n));
}
